import React, { useState } from 'react';
import { AlertCircle, CheckCircle, ExternalLink, Eye, EyeOff, Loader2 } from 'lucide-react';
import { SelectField } from "components/ui/select-field";

interface ZohoCRMIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const ZohoCRMIntegrationSetup: React.FC<ZohoCRMIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [_testResult, setTestResult] = useState<any>(null);
  const [showClientSecret, setShowClientSecret] = useState(false);
  
  const [formData, setFormData] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: '',
    dataCenter: 'com',
    scopes: ['ZohoCRM.modules.ALL', 'ZohoCRM.users.READ', 'ZohoCRM.org.READ']
  });

  const dataCenters = [
    { value: 'com', label: 'US (.com)', domain: 'zohoapis.com' },
    { value: 'eu', label: 'Europe (.eu)', domain: 'zohoapis.eu' },
    { value: 'in', label: 'India (.in)', domain: 'zohoapis.in' },
    { value: 'com.au', label: 'Australia (.com.au)', domain: 'zohoapis.com.au' },
    { value: 'jp', label: 'Japan (.jp)', domain: 'zohoapis.jp' }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.clientId) {
      setError('Client ID is required');
      return false;
    }
    if (!formData.clientSecret) {
      setError('Client Secret is required');
      return false;
    }
    if (!formData.redirectUri) {
      setError('Redirect URI is required');
      return false;
    }
    return true;
  };

  const testConnection = async () => {
    if (!validateStep1()) return;

    setLoading(true);
    setError('');
    setTestResult(null);

    try {
      // Mock connection test for now - replace with actual implementation later
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      
      const result = {
        success: true,
        message: 'Configuration validated successfully'
      };
      
      setTestResult(result);
      
      if (result.success) {
        setStep(2);
      } else {
        setError(result.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const createIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      // Create a mock integration object that matches what IntegrationDashboard expects
      const mockIntegration = {
        getConfig: () => ({
          settings: {
            customSettings: {
              dataCenter: formData.dataCenter,
              clientId: formData.clientId,
              redirectUri: formData.redirectUri,
              scopes: formData.scopes
            }
          }
        })
      };

      onIntegrationCreated(mockIntegration);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Zoho CRM Connection</h3>
        <p className="text-content-secondary">
          Connect your Zoho CRM account to sync contacts, accounts, and deals with your email marketing platform.
        </p>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Before you start</h4>
            <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
              You'll need to create a Zoho application first:
            </p>
            <ol className="text-blue-800 dark:text-blue-300 text-sm mt-2 list-decimal list-inside">
              <li>Go to <strong>Zoho API Console</strong> (api-console.zoho.com)</li>
              <li>Create a new <strong>Server-based Application</strong></li>
              <li>Configure OAuth settings and scopes</li>
              <li>Note your Client ID and Client Secret</li>
            </ol>
            <a
              href="https://api-console.zoho.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 text-sm mt-2"
            >
              Open Zoho API Console <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Data Center *
        </label>
        <SelectField
          value={formData.dataCenter}
          onChange={(e) => handleInputChange('dataCenter', e.target.value)}
          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {dataCenters.map((dc) => (
            <option key={dc.value} value={dc.value}>
              {dc.label} - {dc.domain}
            </option>
          ))}
        </SelectField>
        <p className="text-xs text-content-muted mt-1">
          Select the data center where your Zoho CRM account is hosted
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client ID *
          </label>
          <input
            type="text"
            value={formData.clientId}
            onChange={(e) => handleInputChange('clientId', e.target.value)}
            placeholder="Enter Zoho app Client ID"
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Client Secret *
          </label>
          <div className="relative">
            <input
              type={showClientSecret ? 'text' : 'password'}
              value={formData.clientSecret}
              onChange={(e) => handleInputChange('clientSecret', e.target.value)}
              placeholder="Enter Zoho app Client Secret"
              className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowClientSecret(!showClientSecret)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-content-subtle hover:text-content-secondary"
            >
              {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Redirect URI *
        </label>
        <input
          type="url"
          value={formData.redirectUri}
          onChange={(e) => handleInputChange('redirectUri', e.target.value)}
          placeholder="https://your-domain.com/oauth/zoho/callback"
          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <p className="text-xs text-content-muted mt-1">
          This must match exactly with the redirect URI configured in your Zoho app
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          OAuth Scopes
        </label>
        <div className="bg-gray-50 dark:bg-dark-700 p-3 rounded-lg">
          <div className="space-y-2">
            {formData.scopes.map((scope, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-300">{scope}</span>
                <span className="text-xs text-content-muted">Required</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-content-muted mt-1">
          These scopes will be requested during OAuth authorization
        </p>
      </div>

      <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
        <h4 className="font-medium text-content mb-2">What will be synced?</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <h5 className="font-medium text-gray-700 dark:text-gray-300">Contacts</h5>
            <ul className="text-content-secondary mt-1 list-disc list-inside">
              <li>Contact information</li>
              <li>Email addresses</li>
              <li>Phone numbers</li>
              <li>Custom fields</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-700 dark:text-gray-300">Accounts</h5>
            <ul className="text-content-secondary mt-1 list-disc list-inside">
              <li>Company details</li>
              <li>Account information</li>
              <li>Custom fields</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-700 dark:text-gray-300">Activities</h5>
            <ul className="text-content-secondary mt-1 list-disc list-inside">
              <li>Email engagement</li>
              <li>Campaign interactions</li>
              <li>Activity timeline</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Cancel
        </button>
        <button
          onClick={testConnection}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Test Configuration
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Configuration Valid!</h3>
        <p className="text-content-secondary">
          Your Zoho CRM integration configuration is ready. You'll need to complete OAuth authorization after creation.
        </p>
      </div>

      <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
          <p className="text-green-800 dark:text-green-300 font-medium">Configuration Validated</p>
        </div>
        <div className="text-sm text-green-700 dark:text-green-300">
          <p><strong>Data Center:</strong> {dataCenters.find(dc => dc.value === formData.dataCenter)?.label}</p>
          <p><strong>Client ID:</strong> {formData.clientId.substring(0, 8)}...</p>
        </div>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-900 dark:text-yellow-300">Next Steps</h4>
            <p className="text-yellow-800 dark:text-yellow-300 text-sm mt-1">
              After creating the integration, you'll need to:
            </p>
            <ol className="text-yellow-800 dark:text-yellow-300 text-sm mt-2 list-decimal list-inside">
              <li>Complete OAuth authorization with Zoho</li>
              <li>Grant the required permissions</li>
              <li>Configure field mappings</li>
              <li>Test the data synchronization</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Integration Features</h4>
            <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
              This integration will sync:
            </p>
            <ul className="text-blue-800 dark:text-blue-300 text-sm mt-2 list-disc list-inside">
              <li>Contacts and Accounts bidirectionally</li>
              <li>Email engagement activities to Zoho CRM</li>
              <li>Deal and opportunity updates</li>
              <li>Custom field mappings</li>
              <li>Real-time webhook notifications</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => setStep(1)}
          className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
        >
          Back
        </button>
        <button
          onClick={createIntegration}
          disabled={loading}
          className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Create Integration
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-content">Zoho CRM Integration Setup</h2>
          <div className="flex items-center gap-2 text-sm text-content-secondary">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              1
            </span>
            <span className="w-8 h-px bg-gray-300"></span>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
            }`}>
              2
            </span>
          </div>
        </div>
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};